import React, { useState } from 'react';
import { X, UsersRound, Trash2, Send, Clock, MailCheck, MailX, MailWarning, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { TeamsAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const EMAIL_BADGE = {
  sent: { icon: MailCheck, text: 'Email sent', cls: 'text-[#0AA24F]' },
  failed: { icon: MailX, text: 'Email failed', cls: 'text-[#E5387B]' },
  not_configured: { icon: MailWarning, text: 'Email delivery not configured', cls: 'text-[#B7791F]' },
};

const EmailStatus = ({ status }) => {
  const b = EMAIL_BADGE[status];
  if (!b) return null;
  const Icon = b.icon;
  return <span className={`flex items-center gap-1 text-[11px] ${b.cls}`} data-testid={`email-status-${status}`}><Icon className="w-3 h-3" /> {b.text}</span>;
};

const TeamMembersModal = ({ team, onClose, onUpdated }) => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Member');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  if (!team) return null;
  const isOwner = team.owner_id === user?.user_id;

  const describe = (updated, target) => {
    const m = updated.members.find((x) => x.email === target.toLowerCase());
    if (m?.invite_email_status === 'sent') toast.success(`Invitation email sent to ${target}`);
    else if (m?.invite_email_status === 'failed') toast.warning(`${target} was added, but the invitation email could not be delivered`);
    else toast.info(`${target} added — email delivery is not configured, share the login link manually`);
  };

  const invite = async () => {
    if (!email.trim().includes('@')) { toast.error('Enter a valid email address'); return; }
    setLoading(true);
    try {
      const updated = await TeamsAPI.invite(team.id, email.trim(), role);
      onUpdated(updated);
      describe(updated, email.trim());
      setEmail('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to invite');
    } finally {
      setLoading(false);
    }
  };

  const resend = async (m) => {
    setResending(m.email);
    try { const updated = await TeamsAPI.resendInvite(team.id, m.email); onUpdated(updated); describe(updated, m.email); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to resend'); }
    finally { setResending(null); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from ${team.name}?`)) return;
    try {
      onUpdated(await TeamsAPI.removeMember(team.id, m.email));
      toast.success('Member removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove member');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="team-members-modal">
      <div className="absolute inset-0 bg-[#0A1551]/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E3E5F5] flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: team.color + '20' }}>
            <UsersRound className="w-5 h-5" style={{ color: team.color }} />
          </div>
          <div>
            <h2 className="text-[18px] font-bold text-[#0A1551]">{team.name} · Members</h2>
            <p className="text-[13px] text-[#6F76A7]">{team.members.length} member{team.members.length === 1 ? '' : 's'}</p>
          </div>
          <button onClick={onClose} className="ml-auto p-2 rounded-lg hover:bg-[#F5F6FB]" aria-label="Close"><X className="w-4 h-4 text-[#6F76A7]" /></button>
        </div>

        <div className="px-6 py-4 border-b border-[#E3E5F5] bg-[#F9FAFD]">
          <label className="block text-[13px] font-semibold text-[#0A1551] mb-1.5">Invite a teammate by email</label>
          <div className="flex gap-2">
            <input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && invite()} placeholder="teammate@company.com"
              className="flex-1 px-3 py-2 border border-[#D5D7ED] rounded-md text-[13px] bg-white focus:outline-none focus:border-[#0A1551]" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="px-2 py-2 border border-[#D5D7ED] rounded-md text-[13px] bg-white"><option>Admin</option><option>Member</option></select>
            <button onClick={invite} disabled={loading} data-testid="invite-submit" className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[13px] font-semibold disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> Invite
            </button>
          </div>
          <p className="text-[12px] text-[#6F76A7] mt-1.5">We email them a link; they join automatically the first time they sign in with this address.</p>
        </div>

        <div className="max-h-[50vh] overflow-y-auto divide-y divide-[#F0F1FB]">
          {team.members.map((m) => (
            <div key={m.email} className="flex items-center gap-3 px-6 py-3" data-testid={`member-row-${m.email}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[13px] font-bold" style={{ backgroundColor: m.avatar_color || team.color }}>
                {(m.name || m.email).charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#0A1551] truncate">{m.name || m.email}</div>
                <div className="text-[12px] text-[#6F76A7] truncate">{m.email}</div>
                <EmailStatus status={m.invite_email_status} />
              </div>
              {m.status === 'pending' ? (
                <span className="flex items-center gap-1 text-[11px] font-semibold text-[#B7791F] bg-[#FFF7E0] px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Pending</span>
              ) : (
                <span className="text-[11px] font-semibold text-[#6F76A7] bg-[#F0F1FB] px-2 py-0.5 rounded-full">{m.role}</span>
              )}
              {m.role !== 'Owner' && (
                <button onClick={() => resend(m)} disabled={resending === m.email} className="p-1.5 rounded hover:bg-[#F0F1FB] text-[#6F76A7] disabled:opacity-50" aria-label={`Resend invitation to ${m.email}`} title="Resend invitation email" data-testid={`resend-invite-${m.email}`}>
                  <RefreshCw className={`w-4 h-4 ${resending === m.email ? 'animate-spin' : ''}`} />
                </button>
              )}
              {m.role !== 'Owner' && (isOwner || m.user_id === user?.user_id) && (
                <button onClick={() => remove(m)} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#E5387B]" aria-label={`Remove ${m.email}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamMembersModal;
